import {Box, Card, CardContent, Grid, Table, TableBody, TableCell, TableRow, Typography} from '@mui/material';
import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {i18n} from 'src/i18n';
import authSelectors from 'src/modules/auth/authSelectors';
import {resolveMedicionesParcialForDisplay} from 'src/modules/tarea/medicionesParcialDisplay';
import {formatMedicionesParcialValue} from 'src/modules/tarea/medicionesParcialId23';
import {
  CONTROL_VISUAL_GOMA_TEFLON_OPTIONS,
  hasMedicionesParcialCheckbox,
  TIPO_EMBALAJE_OPTIONS,
} from 'src/modules/tarea/medicionesParcialId24';

const cellSx = {
  border: '1px solid',
  borderColor: 'divider',
  p: 0.75,
  verticalAlign: 'middle',
  fontSize: 13,
};

const labelCellSx = {
  ...cellSx,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  width: 72,
};

const titleSx = {fontWeight: 700, fontSize: 13, textTransform: 'uppercase', mb: 0.5};

function ValueCell({value, notApplicable}: {value: unknown; notApplicable?: boolean}) {
  return <TableCell sx={cellSx}>{formatMedicionesParcialValue(value, notApplicable)}</TableCell>;
}

function SectionTable({title, children}: {title: string; children: React.ReactNode}) {
  return (
    <Box>
      <Typography sx={titleSx as any}>{title}</Typography>
      <Table size="small" sx={{borderCollapse: 'collapse'}}>
        <TableBody>{children}</TableBody>
      </Table>
    </Box>
  );
}

function CheckboxList({
  options,
  labels,
  selected,
}: {
  options: readonly string[];
  labels: Record<string, string>;
  selected: string[] | null | undefined;
}) {
  return (
    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, px: 0.5, py: 0.5}}>
      {options.map((option) => (
        <Typography key={option} sx={{fontSize: 13, fontWeight: 600}}>
          {hasMedicionesParcialCheckbox(selected, option) ? '[x]' : '[ ]'} {labels[option] || option}
        </Typography>
      ))}
    </Box>
  );
}

export default function MedicionesParcialReadonlyView({tasks}: {tasks: any[]}) {
  const currentTenant = useSelector(authSelectors.selectCurrentTenant);
  const display = useMemo(
    () => resolveMedicionesParcialForDisplay(tasks, currentTenant?.id),
    [tasks, currentTenant?.id],
  );

  if (!display.kind || !display.values) return null;

  if (display.kind === 'id23') {
    const values = display.values;
    return (
      <Card variant="outlined" sx={{mb: 2}}>
        <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SectionTable title={i18n('entities.tarea.medicionesParcial.termocontraccion')}>
              <TableRow>
                <TableCell sx={labelCellSx as any}>{i18n('entities.tarea.medicionesParcial.termocontraccionT')}</TableCell>
                <ValueCell value={values.termocontraccion_t} notApplicable={Boolean(values.na_termocontraccion)} />
              </TableRow>
              <TableRow>
                <TableCell sx={labelCellSx as any}>{i18n('entities.tarea.medicionesParcial.termocontraccionL')}</TableCell>
                <ValueCell value={values.termocontraccion_l} notApplicable={Boolean(values.na_termocontraccion)} />
              </TableRow>
            </SectionTable>
          </Grid>
          <Grid item xs={12} md={6}>
            <Table size="small" sx={{borderCollapse: 'collapse'}}>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx as any}>{i18n('entities.tarea.medicionesParcial.cof')}</TableCell>
                  <TableCell sx={{...cellSx, fontWeight: 700, textAlign: 'center'}}>
                    {i18n('entities.tarea.medicionesParcial.estatico')}
                  </TableCell>
                  <TableCell sx={{...cellSx, fontWeight: 700, textAlign: 'center'}}>
                    {i18n('entities.tarea.medicionesParcial.dinamico')}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={labelCellSx as any}>{i18n('entities.tarea.medicionesParcial.interno')}</TableCell>
                  <ValueCell value={values.cof_interno_estatico} notApplicable={Boolean(values.na_cof)} />
                  <ValueCell value={values.cof_interno_dinamico} notApplicable={Boolean(values.na_cof)} />
                </TableRow>
                <TableRow>
                  <TableCell sx={labelCellSx as any}>{i18n('entities.tarea.medicionesParcial.externo')}</TableCell>
                  <ValueCell value={values.cof_externo_estatico} notApplicable={Boolean(values.na_cof)} />
                  <ValueCell value={values.cof_externo_dinamico} notApplicable={Boolean(values.na_cof)} />
                </TableRow>
              </TableBody>
            </Table>
          </Grid>
          <Grid item xs={12} md={6}>
            <SectionTable title={i18n('entities.tarea.medicionesParcial.tensionBobinado')}>
              <TableRow>
                <TableCell sx={labelCellSx as any}>{i18n('entities.tarea.medicionesParcial.inicio')}</TableCell>
                <ValueCell value={values.tension_bobinado_inicio} notApplicable={Boolean(values.na_tension_bobinado)} />
              </TableRow>
              <TableRow>
                <TableCell sx={labelCellSx as any}>{i18n('entities.tarea.medicionesParcial.termino')}</TableCell>
                <ValueCell value={values.tension_bobinado_termino} notApplicable={Boolean(values.na_tension_bobinado)} />
              </TableRow>
            </SectionTable>
          </Grid>
          <Grid item xs={12} md={6}>
            <SectionTable title={i18n('entities.tarea.medicionesParcial.corrienteEstatica')}>
              <TableRow>
                <TableCell sx={labelCellSx as any}>{i18n('entities.tarea.medicionesParcial.inicio')}</TableCell>
                <ValueCell
                  value={values.corriente_estatica_inicio}
                  notApplicable={Boolean(values.na_corriente_estatica)}
                />
              </TableRow>
              <TableRow>
                <TableCell sx={labelCellSx as any}>{i18n('entities.tarea.medicionesParcial.termino')}</TableCell>
                <ValueCell
                  value={values.corriente_estatica_termino}
                  notApplicable={Boolean(values.na_corriente_estatica)}
                />
              </TableRow>
            </SectionTable>
          </Grid>
          <Grid item xs={12} md={6}>
            <SectionTable title={i18n('entities.tarea.medicionesParcial.productoAFabricar')}>
              <TableRow>
                <ValueCell value={values.producto_a_fabricar} />
              </TableRow>
            </SectionTable>
          </Grid>
          <Grid item xs={12} md={6}>
            <SectionTable title={i18n('entities.tarea.medicionesParcial.maquina')}>
              <TableRow>
                <ValueCell value={values.maquina} />
              </TableRow>
            </SectionTable>
          </Grid>
          <Grid item xs={12} md={6}>
            <SectionTable title={i18n('entities.tarea.medicionesParcial.colorFunda')}>
              <TableRow>
                <ValueCell value={values.color_funda} />
              </TableRow>
            </SectionTable>
          </Grid>
          <Grid item xs={12} md={6}>
            <SectionTable title={i18n('entities.tarea.medicionesParcial.microperforado')}>
              <TableRow>
                <ValueCell value={values.microperforado} />
              </TableRow>
            </SectionTable>
          </Grid>
          <Grid item xs={12} md={6}>
            <SectionTable title={i18n('entities.tarea.medicionesParcial.pesoRollo')}>
              <TableRow>
                <ValueCell value={values.peso_rollo} />
              </TableRow>
            </SectionTable>
          </Grid>
          <Grid item xs={12} md={6}>
            <SectionTable title={i18n('entities.tarea.medicionesParcial.soportaconos')}>
              <TableRow>
                <ValueCell value={values.soportacono} />
              </TableRow>
            </SectionTable>
          </Grid>
        </Grid>
        </CardContent>
      </Card>
    );
  }

  const values = display.values;
  const controlVisualLabels = {
    INICIO: i18n('entities.tarea.medicionesParcial.inicio'),
    TERMINO: i18n('entities.tarea.medicionesParcial.termino'),
  };
  const tipoEmbalajeLabels = {
    SACO: i18n('entities.tarea.medicionesParcial.saco'),
    BOLSA_FUNDA: i18n('entities.tarea.medicionesParcial.bolsaFunda'),
  };

  return (
    <Card variant="outlined" sx={{mb: 2}}>
      <CardContent>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.troquel')}>
            <TableRow>
              <ValueCell value={values.troquel} />
            </TableRow>
          </SectionTable>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.cantPerforaciones')}>
            <TableRow>
              <ValueCell
                value={values.cant_perforaciones}
                notApplicable={Boolean(values.na_cant_perforaciones)}
              />
            </TableRow>
          </SectionTable>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.microperforado')}>
            <TableRow>
              <ValueCell value={values.microperforado} />
            </TableRow>
          </SectionTable>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.tipoSello')}>
            <TableRow>
              <ValueCell value={values.tipo_sello} />
            </TableRow>
          </SectionTable>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.prepicado')}>
            <TableRow>
              <ValueCell value={values.prepicado} notApplicable={Boolean(values.na_prepicado)} />
            </TableRow>
          </SectionTable>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.colorFunda')}>
            <TableRow>
              <ValueCell value={values.color_funda} />
            </TableRow>
          </SectionTable>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionTable title={i18n('entities.tarea.medicionesParcial.maquinaSelladora')}>
            <TableRow>
              <ValueCell value={values.maquina_selladora} />
            </TableRow>
          </SectionTable>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box>
            <Typography sx={titleSx as any}>{i18n('entities.tarea.medicionesParcial.controlVisualGomaTeflon')}</Typography>
            <Box sx={{...cellSx, border: '1px solid', borderColor: 'divider'}}>
              {values.na_control_visual_goma_teflon ? (
                <Typography sx={{fontSize: 13, px: 0.5, py: 0.5}}>{i18n('common.notApplicable')}</Typography>
              ) : (
                <CheckboxList
                  options={CONTROL_VISUAL_GOMA_TEFLON_OPTIONS}
                  labels={controlVisualLabels}
                  selected={values.control_visual_goma_teflon}
                />
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box>
            <Typography sx={titleSx as any}>{i18n('entities.tarea.medicionesParcial.tipoEmbalaje')}</Typography>
            <Box sx={{...cellSx, border: '1px solid', borderColor: 'divider'}}>
              {values.na_tipo_embalaje ? (
                <Typography sx={{fontSize: 13, px: 0.5, py: 0.5}}>{i18n('common.notApplicable')}</Typography>
              ) : (
                <CheckboxList
                  options={TIPO_EMBALAJE_OPTIONS}
                  labels={tipoEmbalajeLabels}
                  selected={values.tipo_embalaje}
                />
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
      </CardContent>
    </Card>
  );
}
